<script setup lang="ts">
import type { TaskFilterStatus } from '~/utils/reportUtils'
import type { ReportFilters } from '~/models/ReportFilters'
import type {
  ContentDetailsActivity,
  ContentDetailsAssignment,
  ContentDetailsResponse,
  ContentDetailsTest,
  ReportContentVisit,
  UserAssignmentData
} from '~/models/Report'
import type { GroupMember } from '~/models/Group'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { AssignmentResponse } from '~/models/Assignment'
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { TabsContent, TabsRoot } from 'reka-ui'
import { useQuery } from '@tanstack/vue-query'
import { getTaskCompletionStatus } from '~/utils/reportUtils'
import { UserRole } from '~/models/User/UserRole'
import useReportsApi from '~/api/reportsApi'
import useAssignmentApi from '~/api/assignmentApi'
import ViewModeToggle from '~/components/report/ViewModeToggle.vue'
import UserTaskRowSkeleton from '~/components/report/UserTaskRowSkeleton.vue'
import UserTaskRow from '~/components/report/UserTaskRow.vue'
import UserAssignmentRowSkeleton from '~/components/report/UserAssignmentRowSkeleton.vue'
import UserAssignmentRow from '~/components/report/UserAssignmentRow.vue'
import UserActivityRowSkeleton from '~/components/report/UserActivityRowSkeleton.vue'
import UserActivityRow from '~/components/report/UserActivityRow.vue'
import TaskTable from '~/components/report/TaskTable.vue'
import StatusFilter from '~/components/report/StatusFilter.vue'
import ReportTabsList from '~/components/report/ReportTabsList.vue'
import OpenActivityDetail from '~/components/report/OpenActivityDetail.vue'
import ContentSummary from '~/components/report/ContentSummary.vue'

const props = defineProps<{
  content: BaseItem
  visit?: ReportContentVisit
  groupMembers?: GroupMember[]
  showActivityDetail?: boolean
  activeSection?: 'main' | 'task' | 'assignment'
  sectionIndex?: number
  selectedUserId?: string
  filters?: ReportFilters
}>()

const emit = defineEmits<{
  openDetail: [section: 'main' | 'task' | 'assignment', index?: number, userId?: string]
  closeDetail: []
}>()

const { t } = useI18n()
const { getReportDetails } = useReportsApi()
const { getAssignmentResponse } = useAssignmentApi()

const { data: taskReportData, error, isPending, isLoading } = useQuery<ContentDetailsResponse>({
  queryKey: computed(() => ['reportDetails', props.content.contentId, props.filters?.selectedGroup?.groupId, props.filters?.selectedPeriod]),
  queryFn: () => getReportDetails(props.content.contentId, props.filters),
  enabled: computed(() => !!props.filters?.selectedGroup?.groupId),
  staleTime: Infinity,
  retry: 1,
})

const { data: assignmentResponses } = useQuery<Record<string, AssignmentResponse[]>>({
  queryKey: computed(() => ['assignmentResponses', props.content.contentId, props.filters?.selectedGroup?.groupId]),
  queryFn: async () => {
    const responses: Record<string, AssignmentResponse[]> = {}

    for (const assignment of taskReportData.value!.assignments) {
      try {
        responses[assignment.assignmentKey] = await getAssignmentResponse(assignment.assignmentKey, [props.filters!.selectedGroup!.groupId])
      } catch {
        responses[assignment.assignmentKey] = []
      }
    }

    return responses
  },
  enabled: computed(() => !!taskReportData.value?.assignments && !!props.filters?.selectedGroup?.groupId),
  staleTime: Infinity,
  retry: 1,
})

const isLoadingData = computed(() => {
  const hasGroupSelected = !!props.filters?.selectedGroup?.groupId
  const isQueryLoading = isPending.value || isLoading.value
  const hasNoData = !taskReportData.value

  return isQueryLoading || (hasGroupSelected && hasNoData)
})

const tasks = computed(() => taskReportData.value?.tests ?? [])

const assignments = computed(() => taskReportData.value?.assignments ?? [])

const visits = computed<ReadonlyArray<ContentDetailsActivity>>(() => {
  if (taskReportData.value?.contents && taskReportData.value.contents.length > 0) {
    return taskReportData.value.contents[0].activities ?? []
  }

  return []
})

const showDetail = computed(() => props.showActivityDetail ?? false)

const currentRelation = computed(() => {
  if (!props.activeSection || props.sectionIndex === undefined) {
    return null
  }

  if (props.activeSection === 'assignment') {
    return assignments.value[props.sectionIndex] || null
  }

  if (props.activeSection === 'task') {
    return tasks.value[props.sectionIndex] || null
  }

  return null
})

const getStoredTab = () => {
  if (props.content?.contentId) {
    const savedTab = sessionStorage.getItem(`reportTab_${props.content.contentId}`)
    if (savedTab) {
      return savedTab
    }

    const contentType = props.content.contentTypeIdentifier

    if (contentType === 'form_writer' && assignments.value.length > 0) {
      const firstAssignment = assignments.value[0]
      return `assignment-${firstAssignment.assignmentKey || 0}`
    }

    if (contentType === 'flyt_task' && tasks.value.length > 0) {
      const firstTask = tasks.value[0]
      return `task-${firstTask.flytUuid || 0}`
    }
  }

  return 'main'
}

const activeTab = ref<string>(getStoredTab())
const taskViewMode = ref<'rows' | 'table'>('rows')
const statusFilter = ref<TaskFilterStatus>('all')
const lastContentId = ref<number | undefined>(props.content?.contentId)

watch(activeTab, (newTab) => {
  if (props.content?.contentId) {
    sessionStorage.setItem(`reportTab_${props.content.contentId}`, newTab)
  }
})

watch(() => props.content?.contentId, (newContentId) => {
  if (newContentId !== lastContentId.value) {
    lastContentId.value = newContentId
    if (newContentId) {
      const savedTab = sessionStorage.getItem(`reportTab_${newContentId}`)
      if (savedTab) {
        activeTab.value = savedTab
      } else {
        const contentType = props.content?.contentTypeIdentifier

        if (contentType === 'form_writer' && assignments.value.length > 0) {
          const firstAssignment = assignments.value[0]
          activeTab.value = `assignment-${firstAssignment.assignmentKey || 0}`
        } else if (contentType === 'flyt_task' && tasks.value.length > 0) {
          const firstTask = tasks.value[0]
          activeTab.value = `task-${firstTask.flytUuid || 0}`
        } else {
          activeTab.value = 'main'
        }
      }
    }
  }
})

watch(() => [props.activeSection, props.sectionIndex, tasks.value, assignments.value], () => {
  if (!props.showActivityDetail && props.activeSection && props.activeSection !== 'main') {
    if (props.activeSection === 'task' && props.sectionIndex !== undefined) {
      const task = tasks.value[props.sectionIndex]
      const tabId = `task-${task?.flytUuid || props.sectionIndex}`
      activeTab.value = tabId
    } else if (props.activeSection === 'assignment' && props.sectionIndex !== undefined) {
      const assignment = assignments.value[props.sectionIndex]
      const tabId = `assignment-${assignment?.assignmentKey || props.sectionIndex}`
      activeTab.value = tabId
    }
  }
}, { immediate: true })

watch([tasks, assignments], () => {
  if (!props.content?.contentId)
    return

  const currentStoredTab = sessionStorage.getItem(`reportTab_${props.content.contentId}`)
  if (currentStoredTab || activeTab.value !== 'main')
    return

  const contentType = props.content.contentTypeIdentifier

  if (contentType === 'form_writer' && assignments.value.length > 0) {
    const firstAssignment = assignments.value[0]
    activeTab.value = `assignment-${firstAssignment.assignmentKey || 0}`
  } else if (contentType === 'flyt_task' && tasks.value.length > 0) {
    const firstTask = tasks.value[0]
    activeTab.value = `task-${firstTask.flytUuid || 0}`
  }
})

watch(activeTab, () => {
  statusFilter.value = 'all'
})

function openDetail(section: 'main' | 'task' | 'assignment', index?: number, userId?: string) {
  emit('openDetail', section, index, userId)
}

function closeDetail() {
  emit('closeDetail')
}

function handleStudentClick(userId: string, section: 'main' | 'task' | 'assignment', index?: number) {
  openDetail(section, index, userId)
}

function getMaxTaskCount(test: ContentDetailsTest): number {
  let maxTaskCount = 0
  test.testActivities.forEach((activity) => {
    if (activity.items && activity.items.length > 0) {
      maxTaskCount = Math.max(maxTaskCount, activity.items.length)
    }
  })
  return maxTaskCount || 5
}
function getStudentStatus(groupMember: GroupMember, test: ContentDetailsTest): 'completed' | 'started' | 'notStarted' {
  const userActivity = test.testActivities.find(activity => String(activity?.user?.userId) === String(groupMember.userId))
  if (!userActivity) {
    return 'notStarted'
  }

  const userTaskData = {
    userId: String(groupMember.userId),
    status: 'notStarted' as const,
    items: userActivity.items || [],
    lastActivity: userActivity.providedTimestamp || '',
    attempts: 0
  }

  return getTaskCompletionStatus(userTaskData)
}

const students = computed(() => props.groupMembers?.filter(member => member.role !== UserRole.Teacher) || [])

function findUserVisit(userId: string) {
  return visits.value.find(visit => String(visit?.user?.userId) === String(userId))
}

function buildUserTaskData(groupMember: GroupMember, test: ContentDetailsTest) {
  const userActivity = test.testActivities.find(activity => String(activity?.user?.userId) === String(groupMember.userId))

  if (!userActivity) {
    return {
      userId: groupMember.userId,
      status: 'notStarted' as const,
      items: [],
      lastActivity: '',
      attempts: 0,
    }
  }

  const items = userActivity.items || []
  const totalScore = items.reduce((sum, item) => sum + item.score, 0)
  const totalPossible = items.reduce((sum, item) => sum + item.targetScore, 0)
  const isPass = totalScore > 0 && totalScore >= (totalPossible * 0.5)

  const userTaskData = {
    userId: String(groupMember.userId),
    status: 'notStarted' as const,
    items: userActivity.items || [],
    lastActivity: userActivity.providedTimestamp || '',
    attempts: 0
  }

  const completionStatus = getTaskCompletionStatus(userTaskData)
  const taskDataStatus: 'completed' | 'inProgress' | 'notStarted'
    = completionStatus === 'started' ? 'inProgress' : completionStatus

  return {
    userId: groupMember.userId,
    status: taskDataStatus,
    items,
    lastActivity: userActivity.providedTimestamp || '',
    attempts: 1,
    isPass,
    score: totalScore,
    totalQuestions: totalPossible,
    user: userActivity.user,
  }
}

function getFilteredStudentsForTask(test: ContentDetailsTest) {
  return students.value.filter((member) => {
    if (statusFilter.value === 'all')
      return true
    const studentStatus = getStudentStatus(member, test)
    return studentStatus === statusFilter.value
  })
}

function getFilteredStudentsForAssignment(assignment: ContentDetailsAssignment) {
  return students.value.filter((member) => {
    if (statusFilter.value === 'all')
      return true

    const userAssignment = getUserAssignmentData(assignment.assignmentKey, member.userId)
    const hasData = userAssignment.data && userAssignment.data.trim().length > 0
    const hasActivity = !!userAssignment.lastActivity

    if (statusFilter.value === 'completed')
      return hasData
    if (statusFilter.value === 'started')
      return hasActivity && !hasData
    if (statusFilter.value === 'notStarted')
      return !hasActivity

    return true
  })
}

function getUserAssignmentData(assignmentKey: string, userId: string): UserAssignmentData {
  const responses = assignmentResponses.value?.[assignmentKey] || []

  const userResponse = responses.find(response =>
    String(response.student.userId) === String(userId)
  )

  if (!userResponse) {
    return {
      userId,
      data: '',
      lastActivity: '',
    }
  }

  const nonDraftAnswers = userResponse.answer.filter(answer => !answer.isDraft)
  const latestAnswer = nonDraftAnswers.length > 0
    ? nonDraftAnswers[nonDraftAnswers.length - 1]
    : userResponse.answer[userResponse.answer.length - 1]

  return {
    userId,
    data: latestAnswer?.data || '',
    lastActivity: latestAnswer?.modified || latestAnswer?.created || '',
    user: userResponse.student,
  }
}
</script>

<template>
  <div>
    <template v-if="showDetail">
      <OpenActivityDetail
        :content="props.content"
        :visit="props.visit"
        :filters="props.filters"
        :active-section="props.activeSection || 'main'"
        :section-index="props.sectionIndex"
        :selected-user-id="props.selectedUserId"
        :group-members="props.groupMembers"
        :relation="currentRelation"
        @back="closeDetail"
      />
    </template>

    <template v-else>
      <div v-if="error" class="flex items-center justify-center py-8">
        <p>{{ t('error') }}</p>
      </div>

      <div v-else>
        <ContentSummary
          :content="props.content"
          :group-members="props.groupMembers"
          :visit="props.visit"
          :statistics="taskReportData?.statistics"
          :filters="props.filters"
          :tasks="tasks"
          :assignments="assignments"
        />
      </div>

      <div v-if="!error" class="mt-6">
        <TabsRoot
          v-model="activeTab"
          orientation="horizontal"
          activation-mode="manual"
        >
          <ReportTabsList
            v-if="!isLoadingData"
            v-model="activeTab"
            :tasks="tasks"
            :assignments="assignments"
          />

          <TabsContent
            value="main"
            class="space-y-4"
          >
            <template v-if="isLoadingData && !error">
              <UserActivityRowSkeleton
                v-for="groupMember in students"
                :key="`skeleton-${groupMember.userId}`"
              />
            </template>
            <UserActivityRow
              v-for="groupMember in students"
              v-else
              :key="groupMember.userId"
              :group-member="groupMember"
              :user-activity="findUserVisit(groupMember.userId)"
              :group-members="props.groupMembers"
            />
          </TabsContent>

          <TabsContent
            v-for="(test, testIndex) in tasks"
            :key="`task-content-${test.flytUuid}`"
            :value="`task-${test.flytUuid || testIndex}`"
            class="space-y-4"
            tabindex="-1"
          >
            <div class="relative flex items-end justify-between">
              <div class="flex items-center">
                <StatusFilter v-model="statusFilter" />
              </div>
              <div class="flex items-end">
                <ViewModeToggle v-model="taskViewMode" />
              </div>
            </div>
            <template v-if="taskViewMode === 'table'">
              <div v-if="isLoadingData && !error" class="space-y-4">
                <UserTaskRowSkeleton
                  v-for="groupMember in students"
                  :key="`table-skeleton-${groupMember.userId}`"
                  :task-count="5"
                />
              </div>
              <TaskTable
                v-else
                :filters="props.filters"
                :test="test"
                :status-filter="statusFilter"
              />
            </template>

            <div v-else class="space-y-4 px-2">
              <template v-if="isLoadingData && !error">
                <UserTaskRowSkeleton
                  v-for="groupMember in students"
                  :key="`task-skeleton-${groupMember.userId}`"
                  :task-count="5"
                />
              </template>
              <UserTaskRow
                v-for="groupMember in getFilteredStudentsForTask(test)"
                v-else
                :key="groupMember.userId"
                :total-task-count="getMaxTaskCount(test)"
                :user-task="buildUserTaskData(groupMember, test)"
                :filters="props.filters"
                @click="handleStudentClick(groupMember.userId, 'task', testIndex)"
              />
            </div>
          </TabsContent>

          <TabsContent
            v-for="(assignment, assignmentIndex) in assignments"
            :key="`assignment-content-${assignment.assignmentKey}`"
            :value="`assignment-${assignment.assignmentKey || assignmentIndex}`"
            class="space-y-4"
            tabindex="-1"
          >
            <div class="relative">
              <div class="flex">
                <StatusFilter v-model="statusFilter" :show-started="false" />
              </div>
            </div>

            <div class="space-y-4 px-2">
              <template v-if="isLoadingData && !error">
                <UserAssignmentRowSkeleton
                  v-for="groupMember in students"
                  :key="`assignment-skeleton-${groupMember.userId}`"
                />
              </template>
              <UserAssignmentRow
                v-for="groupMember in getFilteredStudentsForAssignment(assignment)"
                v-else
                :key="`${assignment.assignmentKey}-${groupMember.userId}`"
                :user-assignment="getUserAssignmentData(assignment.assignmentKey, groupMember.userId)"
                :filters="props.filters"
                :assignment-id="assignment.assignmentKey"
                @click="handleStudentClick(groupMember.userId, 'assignment', assignmentIndex)"
              />
            </div>
          </TabsContent>
        </TabsRoot>
      </div>
    </template>
  </div>
</template>
